/**
 * CommandPalette: shared ⌘K command palette for librarian and admin dashboards.
 *
 * Opens on:  Ctrl+K / ⌘K  or  window event 'kg:palette:open'
 * Closes on: Escape, backdrop click, item selection
 *
 * Usage:
 *   <CommandPalette items={LIBRARIAN_NAV} />   // mount once in layout
 *
 * To open programmatically (e.g. from a topbar button):
 *   window.dispatchEvent(new CustomEvent('kg:palette:open'));
 */

import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, CornerDownLeft, ArrowUp, ArrowDown } from 'lucide-react';

export interface PaletteItem {
  label: string;
  href: string;
  /** Icon component (lucide) */
  icon?: React.ComponentType<{ className?: string }>;
  /** Optional shortcut hint shown in the row, e.g. "G B" */
  shortcut?: string;
  /** Optional secondary text (group / section name) */
  group?: string;
}

interface CommandPaletteProps {
  items: PaletteItem[];
  /** Extra CSS class for the dialog panel */
  className?: string;
  /** Token set: 'lib' (librarian vars) | 'admin' (plain tailwind) */
  variant?: 'lib' | 'admin';
}

/** Score for fuzzy matching: higher is better match. */
function score(query: string, label: string): number {
  const q = query.toLowerCase();
  const l = label.toLowerCase();
  if (l === q) return 3;
  if (l.startsWith(q)) return 2;
  if (l.includes(q)) return 1;
  return 0;
}

const OPEN_EVENT = 'kg:palette:open';

const CommandPalette: React.FC<CommandPaletteProps> = ({
  items,
  className = '',
  variant = 'lib',
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [cursor, setCursor] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  const close = useCallback(() => {
    setOpen(false);
    setQuery('');
    setCursor(0);
  }, []);

  const doOpen = useCallback(() => {
    setOpen(true);
    setQuery('');
    setCursor(0);
    setTimeout(() => inputRef.current?.focus(), 30);
  }, []);

  /* Global keydown & custom event */
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        open ? close() : doOpen();
      }
      if (e.key === 'Escape' && open) {
        close();
      }
    };
    const onEvent = () => doOpen();
    window.addEventListener('keydown', onKey);
    window.addEventListener(OPEN_EVENT, onEvent);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener(OPEN_EVENT, onEvent);
    };
  }, [open, close, doOpen]);

  /* Filtered + sorted list */
  const filtered = query.trim()
    ? items
        .map((it) => ({ it, s: score(query, it.label) + (it.group ? score(query, it.group) * 0.5 : 0) }))
        .filter(({ s }) => s > 0)
        .sort((a, b) => b.s - a.s)
        .map(({ it }) => it)
    : items;

  /* Keyboard navigation inside palette */
  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setCursor((c) => Math.min(c + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setCursor((c) => Math.max(c - 1, 0));
    } else if (e.key === 'Enter') {
      const item = filtered[cursor];
      if (item) { navigate(item.href); close(); }
    }
  };

  const pick = (item: PaletteItem) => {
    navigate(item.href);
    close();
  };

  /* Reset cursor when filter changes */
  useEffect(() => { setCursor(0); }, [query]);

  if (!open) return null;

  const isLib = variant === 'lib';

  const panelCls = isLib
    ? 'bg-[var(--lib-surface)] border border-[var(--lib-hairline)] text-[var(--lib-ink)]'
    : 'bg-white border border-gray-200 text-gray-900';

  const inputCls = isLib
    ? 'text-[var(--lib-ink)] placeholder:text-[var(--lib-ink-muted)] caret-[var(--lib-gold)]'
    : 'text-gray-900 placeholder:text-gray-400';

  const dividerCls = isLib ? 'border-[var(--lib-hairline)]' : 'border-gray-100';

  const rowActiveCls = isLib
    ? 'bg-[var(--lib-surface-2)] text-[var(--lib-ink)]'
    : 'bg-amber-50 text-amber-700';

  const rowCls = isLib
    ? 'text-[var(--lib-ink-muted)] hover:bg-[var(--lib-surface-2)] hover:text-[var(--lib-ink)]'
    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900';

  const shortcutCls = isLib
    ? 'text-[var(--lib-ink-muted)] bg-[var(--lib-hairline)]'
    : 'text-gray-400 bg-gray-100';

  const footerCls = isLib
    ? 'bg-[var(--lib-surface-2)] border-t border-[var(--lib-hairline)] text-[var(--lib-ink-muted)]'
    : 'bg-gray-50 border-t border-gray-100 text-gray-400';

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-start justify-center pt-[18vh]"
      role="dialog"
      aria-modal="true"
      aria-label="Command palette"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        onClick={close}
        aria-hidden="true"
      />

      {/* Panel */}
      <div
        className={`relative w-full max-w-[560px] rounded-xl shadow-2xl overflow-hidden ${panelCls} ${className}`}
        onKeyDown={onKeyDown}
      >
        {/* Search row */}
        <div className={`flex items-center gap-3 px-4 py-3 border-b ${dividerCls}`}>
          <Search className={`h-4 w-4 flex-shrink-0 ${isLib ? 'text-[var(--lib-ink-muted)]' : 'text-gray-400'}`} />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search or jump to…"
            className={`flex-1 bg-transparent text-[14px] focus:outline-none ${inputCls}`}
            autoComplete="off"
            spellCheck={false}
          />
          <kbd className={`text-[11px] font-mono px-1.5 py-0.5 rounded ${shortcutCls}`}>Esc</kbd>
        </div>

        {/* Results */}
        <ul className="overflow-y-auto max-h-[360px] py-1" role="listbox">
          {filtered.length === 0 && (
            <li className={`px-4 py-10 text-center text-[13px] ${isLib ? 'text-[var(--lib-ink-muted)]' : 'text-gray-400'}`}>
              No results for <span className="font-semibold">"{query}"</span>
            </li>
          )}
          {filtered.map((item, i) => {
            const Icon = item.icon;
            const active = i === cursor;
            return (
              <li
                key={item.href}
                role="option"
                aria-selected={active}
                onMouseEnter={() => setCursor(i)}
                onClick={() => pick(item)}
                className={`flex items-center gap-3 px-4 py-2.5 cursor-pointer transition-colors ${active ? rowActiveCls : rowCls}`}
              >
                {Icon && (
                  <Icon className={`h-4 w-4 flex-shrink-0 ${active ? (isLib ? 'text-[var(--lib-gold)]' : 'text-amber-500') : ''}`} />
                )}
                <div className="flex-1 min-w-0">
                  <span className="text-[13px] font-medium">{item.label}</span>
                  {item.group && (
                    <span className={`ml-2 text-[11px] font-mono uppercase tracking-wider ${isLib ? 'text-[var(--lib-ink-muted)]' : 'text-gray-400'}`}>
                      {item.group}
                    </span>
                  )}
                </div>
                {item.shortcut && (
                  <div className="flex items-center gap-1 flex-shrink-0">
                    {item.shortcut.split(' ').map((k) => (
                      <kbd key={k} className={`text-[11px] font-mono px-1.5 py-0.5 rounded ${shortcutCls}`}>{k}</kbd>
                    ))}
                  </div>
                )}
                {active && (
                  <CornerDownLeft className={`h-3.5 w-3.5 flex-shrink-0 ${isLib ? 'text-[var(--lib-gold)]' : 'text-amber-500'}`} />
                )}
              </li>
            );
          })}
        </ul>

        {/* Footer hints */}
        <div className={`flex items-center gap-4 px-4 py-2 text-[11px] font-mono ${footerCls}`}>
          <span className="inline-flex items-center gap-1">
            <ArrowUp className="h-3 w-3" /><ArrowDown className="h-3 w-3" /> Navigate
          </span>
          <span className="inline-flex items-center gap-1">
            <CornerDownLeft className="h-3 w-3" /> Open
          </span>
          <span>Esc · Close</span>
          <span className="ml-auto opacity-60">⌘K anywhere</span>
        </div>
      </div>
    </div>
  );
};

export default CommandPalette;
